import { View, Text, styled } from 'tamagui'
import { useState } from 'react'
import { BarChart3 } from '@tamagui/lucide-icons'
import type { PollData } from '@/types'

const PollContainer = styled(View, {
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingHorizontal: 16,
  paddingVertical: 12,
  marginTop: 4,
  maxWidth: 450,
  gap: 8,
})

const PollHeader = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
})

const PollQuestion = styled(Text, {
  fontFamily: '$fontBody',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
  flex: 1,
})

const OptionRow = styled(View, {
  position: 'relative',
  borderRadius: '$borderRadiusSm',
  overflow: 'hidden',
  cursor: 'pointer',
})

const OptionBackground = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  backgroundColor: '$accentLighter',
  borderRadius: '$borderRadiusSm',
  // @ts-expect-error -- web transition
  style: { transition: 'width 300ms ease' },
})

const OptionContent = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: 12,
  paddingVertical: 8,
  position: 'relative',
  zIndex: 1,
  borderWidth: 1,
  borderColor: '$borderDefault',
  borderRadius: '$borderRadiusSm',
  hoverStyle: {
    borderColor: '$accent',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'border-color 150ms ease' },
  variants: {
    selected: {
      true: {
        borderColor: '$accent',
      },
    },
  } as const,
})

const OptionText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$surfaceForeground',
  flex: 1,
})

const VoteCount = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 12,
  color: '$color8',
  marginLeft: 8,
})

const TotalVotes = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$color8',
  marginTop: 4,
})

interface PollMessageProps {
  poll: PollData
}

export function PollMessage({ poll }: PollMessageProps) {
  const [votedOptionId, setVotedOptionId] = useState<string | null>(null)

  const totalVotes = poll.options.reduce((sum, opt) => sum + opt.voterIds.length, 0) + (votedOptionId ? 1 : 0)

  const handleVote = (optionId: string) => {
    if (votedOptionId === optionId) {
      setVotedOptionId(null)
    } else {
      setVotedOptionId(optionId)
    }
  }

  return (
    <PollContainer>
      <PollHeader>
        <BarChart3 size={16} color="$accent" />
        <PollQuestion>{poll.question}</PollQuestion>
      </PollHeader>

      {poll.options.map((option) => {
        const isSelected = votedOptionId === option.id
        const votes = option.voterIds.length + (isSelected ? 1 : 0)
        const pct = totalVotes > 0 ? (votes / totalVotes) * 100 : 0

        return (
          <OptionRow key={option.id} onPress={() => handleVote(option.id)}>
            <OptionBackground
              // @ts-expect-error -- dynamic width
              style={{ width: `${pct}%` }}
            />
            <OptionContent selected={isSelected}>
              <OptionText>{option.text}</OptionText>
              <VoteCount>{votes} {votes === 1 ? 'vote' : 'votes'}</VoteCount>
            </OptionContent>
          </OptionRow>
        )
      })}

      <TotalVotes>{totalVotes} total {totalVotes === 1 ? 'vote' : 'votes'}</TotalVotes>
    </PollContainer>
  )
}
